/**
 * Google Play Billing inside the TWA.
 * Browser stays on Przelewy24. A Play install without Digital Goods must not offer a payment link.
 */

export const TWA_PACKAGE = 'pl.edu.mojamatura.app.twa';
export const TWA_REFERRER = `android-app://${TWA_PACKAGE}`;
export const PLAY_BILLING_METHOD = 'https://play.google.com/billing';

/** Plain text only. A link to the site would be an external payment path. */
export const PLAY_BILLING_UNAVAILABLE_MESSAGE =
  'Zaktualizuj aplikację w sklepie Play lub otwórz app.mojamatura.edu.pl w przeglądarce, żeby zakupić subskrypcję.';
export const PLAY_PRODUCT_IDS = ['premium_30', 'premium_90', 'premium_180', 'premium_360'] as const;

const TWA_SESSION_KEY = 'mm-twa-session';

const DURATION_TO_PRODUCT: Record<number, (typeof PLAY_PRODUCT_IDS)[number]> = {
  30: 'premium_30',
  90: 'premium_90',
  180: 'premium_180',
  360: 'premium_360',
};

export type PlayBillingMode = 'play' | 'unavailable' | 'p24';

export type PurchaseAction = 'p24' | 'play' | 'activate' | 'none' | 'promo-in-browser';

export type PlayPrice = {
  currency: string;
  value: string;
};

type PlayItemDetails = {
  itemId: string;
  price?: { currency?: string; value?: string };
};

type DigitalGoodsService = {
  getDetails: (itemIds: string[]) => Promise<PlayItemDetails[]>;
};

type PlayPaymentResponse = {
  details?: { purchaseToken?: string; token?: string };
  complete: (result: PaymentComplete) => Promise<void>;
};

declare global {
  interface Window {
    getDigitalGoodsService?: (paymentMethod: string) => Promise<DigitalGoodsService>;
  }
}

let digitalGoodsService: DigitalGoodsService | null = null;
let modePromise: Promise<PlayBillingMode> | null = null;

const sessionStore = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/** Remember a Play TWA launch for this tab. `display-mode: standalone` is also a browser PWA. */
export function captureTwaSession(referrer: string = typeof document !== 'undefined' ? document.referrer : ''): void {
  if (referrer !== TWA_REFERRER) return;
  sessionStore()?.setItem(TWA_SESSION_KEY, '1');
}

export function isTwaSession(): boolean {
  return sessionStore()?.getItem(TWA_SESSION_KEY) === '1';
}

export function playProductIdForDuration(duration: number): string | null {
  return DURATION_TO_PRODUCT[duration] ?? null;
}

/**
 * Play sheet, no purchase, or Przelewy24.
 * A percentage promo never starts Play — the store price cannot change.
 * A 100% code still uses the existing checkout.
 */
export function resolvePurchaseAction(input: {
  mode: PlayBillingMode | 'pending';
  paymentRequired: boolean;
  percentPromo: boolean;
}): PurchaseAction {
  if (input.mode === 'pending') return 'none';
  if (!input.paymentRequired) return 'activate';
  if (input.mode === 'p24') return 'p24';
  if (input.percentPromo) return 'promo-in-browser';
  if (input.mode === 'play') return 'play';
  return 'none';
}

/** Hide buttons and links that open the payment screen when this TWA cannot bill. */
export function canLinkToPurchase(mode: PlayBillingMode | 'pending'): boolean {
  if (mode === 'unavailable') return false;
  if (mode === 'pending' && isTwaSession()) return false;
  return true;
}

async function detectPlayBillingMode(): Promise<PlayBillingMode> {
  captureTwaSession();
  const getter = window.getDigitalGoodsService;
  if (typeof getter === 'function') {
    try {
      const service = await getter(PLAY_BILLING_METHOD);
      if (service && typeof service.getDetails === 'function') {
        digitalGoodsService = service;
        return 'play';
      }
    } catch {
      digitalGoodsService = null;
    }
  }
  return isTwaSession() ? 'unavailable' : 'p24';
}

export function resolvePlayBillingMode(): Promise<PlayBillingMode> {
  if (!modePromise) {
    modePromise = detectPlayBillingMode();
  }
  return modePromise;
}

export async function getPlayPrices(itemIds: readonly string[]): Promise<Record<string, PlayPrice>> {
  if (!digitalGoodsService) {
    throw new Error('Play Billing is unavailable');
  }
  const items = await digitalGoodsService.getDetails([...itemIds]);
  return items.reduce<Record<string, PlayPrice>>((prices, item) => {
    if (item?.itemId && item.price?.value) {
      prices[item.itemId] = {
        currency: item.price.currency || 'PLN',
        value: String(item.price.value),
      };
    }
    return prices;
  }, {});
}

export function isPurchaseCancelled(error: unknown): boolean {
  return Boolean(error && typeof error === 'object' && (error as { name?: string }).name === 'AbortError');
}

/** Opens the Play purchase sheet. Access is granted only after the backend accepts the token. */
export async function requestPlayPurchase(
  sku: string,
  amount: PlayPrice,
): Promise<{ purchaseToken: string; acknowledge: (ok: boolean) => Promise<void> }> {
  const request = new PaymentRequest([{ supportedMethods: PLAY_BILLING_METHOD, data: { sku } }], {
    total: {
      label: 'Premium',
      amount: { currency: amount.currency, value: amount.value },
    },
  });
  const response = (await request.show()) as PlayPaymentResponse;
  const purchaseToken = response.details?.purchaseToken || response.details?.token;
  if (!purchaseToken) {
    await response.complete('fail');
    throw new Error('Missing purchase token');
  }
  return {
    purchaseToken,
    acknowledge: (ok: boolean) => response.complete(ok ? 'success' : 'fail'),
  };
}

export function resetPlayBillingForTests(): void {
  digitalGoodsService = null;
  modePromise = null;
  sessionStore()?.removeItem(TWA_SESSION_KEY);
}
